// 2D Canvas aura engine from space-button (3).html
// Blobs, neon ring, rays (outer + inner), particles — audio-reactive

const BSTEPS = 72;
const BAR_N = 72;
const PC = 28;

interface Blob {
  hue: number;
  rot: number;
  rotSpeed: number;
  wSpeed: number;
  waves: { f: number; a: number; p: number }[];
  base: number;
  aph: number;
}

interface Bar {
  angle: number;
  ph1: number;
  ph2: number;
  ph3: number;
}

interface Particle {
  angle: number;
  r: number;
  speed: number;
  phase: number;
  size: number;
  hue: number;
}

export function setupSpaceAura(canvas: HTMLCanvasElement, wrapSize: number) {
  const ctx = canvas.getContext('2d')!;
  const DPR = Math.min(devicePixelRatio, 2);
  canvas.width = wrapSize * DPR;
  canvas.height = wrapSize * DPR;
  ctx.scale(DPR, DPR);

  const W = wrapSize;
  const H = wrapSize;
  const CX = W / 2;
  const CY = H / 2;
  const RING_R = 34;

  const blobs: Blob[] = [
    {
      hue: 272,
      rot: 0,
      rotSpeed: 0.0028,
      wSpeed: 0.9,
      waves: [
        { f: 2, a: 4, p: 0 },
        { f: 3, a: 3, p: 1.1 },
        { f: 5, a: 2, p: 2.4 },
      ],
      base: 34,
      aph: 0,
    },
    {
      hue: 318,
      rot: Math.PI * 0.4,
      rotSpeed: -0.0038,
      wSpeed: 1.1,
      waves: [
        { f: 2, a: 4, p: 0.8 },
        { f: 4, a: 3, p: 2.0 },
        { f: 6, a: 2, p: 3.5 },
      ],
      base: 34,
      aph: 1.7,
    },
  ];

  const bars: Bar[] = Array.from({ length: BAR_N }, (_, i) => ({
    angle: (i / BAR_N) * Math.PI * 2,
    ph1: Math.random() * Math.PI * 2,
    ph2: Math.random() * Math.PI * 2,
    ph3: Math.random() * Math.PI * 2,
  }));

  const particles: Particle[] = Array.from({ length: PC }, () => ({
    angle: Math.random() * Math.PI * 2,
    r: RING_R + 6 + Math.random() * 10,
    speed: (Math.random() - 0.5) * 0.008,
    phase: Math.random() * Math.PI * 2,
    size: 0.8 + Math.random() * 1.0,
    hue: [195, 220, 270, 310][Math.floor(Math.random() * 4)],
  }));

  // ── helpers ──────────────────────────────────────────────────────

  function outerH(b: Bar, t: number, audio: number) {
    const v =
      0.4 * Math.sin(t * 2.3 + b.ph1) +
      0.35 * Math.sin(t * 3.9 + b.ph2) +
      0.25 * Math.sin(t * 6.7 + b.ph3);
    return (1 + (v * 0.5 + 0.5) * 7) * (1 + audio * 3);
  }

  function innerH(b: Bar, t: number, audio: number) {
    const v =
      0.4 * Math.sin(t * 1.7 + b.ph2 + 1.3) +
      0.35 * Math.sin(t * 2.9 + b.ph3 + 2.6) +
      0.25 * Math.sin(t * 5.1 + b.ph1 + 4.2);
    return (0.5 + (v * 0.5 + 0.5) * 4.5) * (1 + audio * 2.5);
  }

  function blobPts(b: Blob, t: number) {
    return Array.from({ length: BSTEPS }, (_, i) => {
      const ang = (i / BSTEPS) * Math.PI * 2 + b.rot;
      let r = b.base;
      for (const w of b.waves) r += w.a * Math.sin(w.f * ang + t * b.wSpeed + w.p);
      r = Math.min(44, Math.max(26, r));
      return { x: CX + Math.cos(ang) * r, y: CY + Math.sin(ang) * r };
    });
  }

  function drawBlob(pts: { x: number; y: number }[], hue: number, alpha: number) {
    ctx.save();
    const n = pts.length;
    ctx.beginPath();
    for (let i = 0; i < n; i++) {
      const p0 = pts[(i - 1 + n) % n];
      const p1 = pts[i];
      const p2 = pts[(i + 1) % n];
      const p3 = pts[(i + 2) % n];
      if (i === 0) ctx.moveTo(p1.x, p1.y);
      ctx.bezierCurveTo(
        p1.x + (p2.x - p0.x) / 6,
        p1.y + (p2.y - p0.y) / 6,
        p2.x - (p3.x - p1.x) / 6,
        p2.y - (p3.y - p1.y) / 6,
        p2.x,
        p2.y
      );
    }
    ctx.closePath();
    const g = ctx.createRadialGradient(CX, CY, 10, CX, CY, 46);
    g.addColorStop(0, `hsla(${hue},90%,60%,0)`);
    g.addColorStop(0.48, `hsla(${hue},90%,62%,${alpha * 0.5})`);
    g.addColorStop(0.75, `hsla(${hue},90%,55%,${alpha * 0.3})`);
    g.addColorStop(1, `hsla(${hue},90%,45%,0)`);
    ctx.fillStyle = g;
    ctx.shadowBlur = 10;
    ctx.shadowColor = `hsla(${hue},100%,68%,${alpha * 0.55})`;
    ctx.fill();
    ctx.lineWidth = 0.7;
    ctx.strokeStyle = `hsla(${hue},100%,78%,${alpha * 0.45})`;
    ctx.shadowBlur = 6;
    ctx.stroke();
    ctx.restore();
  }

  // ── state ────────────────────────────────────────────────────────

  let active = false;
  let fadeAlpha = 0;
  let t = 0;
  let running = false;
  let animId = 0;
  let micI = 0;
  let srvI = 0;

  function frame() {
    fadeAlpha += ((active ? 1 : 0) - fadeAlpha) * 0.09;
    const done = !active && fadeAlpha < 0.008;
    if (done) fadeAlpha = 0;

    ctx.clearRect(0, 0, W, H);

    if (fadeAlpha > 0.005) {
      // 1 — blobs
      for (const b of blobs) {
        b.rot += b.rotSpeed;
        const a = (0.48 + 0.22 * Math.sin(t * 1.8 + b.aph) + srvI * 0.3) * fadeAlpha;
        drawBlob(blobPts(b, t), b.hue, a);
      }

      // 2 — neon ring
      const rHue = 195 + (Math.sin(t * 0.32) * 0.5 + 0.5) * 125;
      ctx.save();
      ctx.beginPath();
      ctx.arc(CX, CY, RING_R, 0, Math.PI * 2);
      ctx.lineWidth = 0.8 + micI * 0.5;
      ctx.strokeStyle = `hsla(${rHue},100%,72%,${(0.7 + micI * 0.3) * fadeAlpha})`;
      ctx.shadowBlur = 14 + micI * 8;
      ctx.shadowColor = `hsla(${rHue},100%,72%,${(0.75 + micI * 0.25) * fadeAlpha})`;
      ctx.stroke();
      ctx.restore();

      // 3 — outer rays
      for (const b of bars) {
        const h = outerH(b, t, micI);
        const frac = Math.min(1, (h - 1) / 7);
        const hue = 195 + frac * 125;
        const a = (0.55 + 0.45 * frac) * fadeAlpha;
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(
          CX + Math.cos(b.angle) * (RING_R + 0.5),
          CY + Math.sin(b.angle) * (RING_R + 0.5)
        );
        ctx.lineTo(
          CX + Math.cos(b.angle) * (RING_R + 0.5 + h),
          CY + Math.sin(b.angle) * (RING_R + 0.5 + h)
        );
        ctx.lineWidth = 0.6;
        ctx.lineCap = 'round';
        ctx.strokeStyle = `hsla(${hue},100%,75%,${a})`;
        ctx.shadowBlur = frac > 0.45 ? 5 : 0;
        ctx.shadowColor = `hsla(${hue},100%,88%,${a * 0.8})`;
        ctx.stroke();
        ctx.restore();
      }

      // 4 — inner rays
      for (const b of bars) {
        const h = innerH(b, t, srvI);
        const frac = Math.min(1, (h - 0.5) / 4.5);
        const hue = 190 + frac * 80;
        const a = (0.65 + 0.35 * frac) * fadeAlpha;
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(
          CX + Math.cos(b.angle) * (RING_R - 0.5),
          CY + Math.sin(b.angle) * (RING_R - 0.5)
        );
        ctx.lineTo(
          CX + Math.cos(b.angle) * (RING_R - 0.5 - h),
          CY + Math.sin(b.angle) * (RING_R - 0.5 - h)
        );
        ctx.lineWidth = 0.5;
        ctx.lineCap = 'round';
        ctx.strokeStyle = `hsla(${hue},100%,78%,${a})`;
        ctx.shadowBlur = 4;
        ctx.shadowColor = `hsla(${hue},100%,90%,${a * 0.9})`;
        ctx.stroke();
        ctx.restore();
      }

      // 5 — particles
      for (const p of particles) {
        p.angle += p.speed + micI * 0.003;
        const pr = p.r + 2 * Math.sin(t * 0.9 + p.phase);
        const pa = (0.5 + 0.5 * Math.sin(t * 2.1 + p.phase)) * fadeAlpha;
        ctx.save();
        ctx.beginPath();
        ctx.arc(CX + Math.cos(p.angle) * pr, CY + Math.sin(p.angle) * pr, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${p.hue},90%,85%,${pa})`;
        ctx.shadowBlur = 5;
        ctx.shadowColor = `hsla(${p.hue},100%,90%,${pa * 0.7})`;
        ctx.fill();
        ctx.restore();
      }
    }

    t += 0.016;
    if (done) {
      running = false;
      return;
    }
    animId = requestAnimationFrame(frame);
  }

  return {
    setActive(v: boolean) {
      active = v;
      if (v && !running) {
        running = true;
        animId = requestAnimationFrame(frame);
      }
    },
    setAudio(mic: number, srv: number) {
      micI = mic;
      srvI = srv;
    },
    dispose() {
      cancelAnimationFrame(animId);
    },
  };
}
